<script setup lang="ts">
  import { getInitials } from '@/lib/utils'
  import type { TUser } from '@/types/generated'
  import { computed, ref, watch } from 'vue'

  const props = withDefaults(
    defineProps<{
      users: TUser[]
      loading?: boolean
      selectedUserUuids: string[]
      selectedEmails?: string[]
      allowEmails?: boolean
      multiple?: boolean
      placeholder: string
      addEmailLabel?: string
      disabled?: boolean
    }>(),
    {
      loading: false,
      selectedEmails: () => [],
      allowEmails: false,
      multiple: true,
      addEmailLabel: 'Als externe E-Mail hinzufügen',
      disabled: false
    }
  )

  const emit = defineEmits<{
    'update:selectedUserUuids': [value: string[]]
    'update:selectedEmails': [value: string[]]
    'update:query': [value: string]
  }>()

  const query = ref('')
  const showSuggestions = ref(false)
  const wrapperRef = ref<HTMLDivElement | null>(null)
  const inputRef = ref<HTMLInputElement | null>(null)
  const activeIndex = ref(0)

  const userLabel = (user: TUser): string =>
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || user.uuid

  const selectedUsers = computed(() =>
    props.selectedUserUuids
      .map((uuid) => props.users.find((user) => user.uuid === uuid))
      .filter((user): user is TUser => Boolean(user))
  )

  const filteredUsers = computed(() => {
    const normalizedQuery = query.value.trim().toLowerCase()

    return props.users
      .filter((user) => {
        if (normalizedQuery.length === 0) {
          return true
        }

        return (
          userLabel(user).toLowerCase().includes(normalizedQuery) ||
          user.email?.toLowerCase().includes(normalizedQuery)
        )
      })
      .filter((user) => !props.selectedUserUuids.includes(user.uuid))
      .slice(0, 8)
  })

  const isEmail = (value: string): boolean => /\S+@\S+\.\S+/.test(value)
  const trimmedQuery = computed(() => query.value.trim())
  const canAddEmail = computed(
    () =>
      props.allowEmails &&
      isEmail(trimmedQuery.value) &&
      !props.selectedEmails.includes(trimmedQuery.value)
  )

  const focusInput = (): void => {
    inputRef.value?.focus()
  }

  const selectUser = (user: TUser): void => {
    if (props.disabled) {
      return
    }

    emit(
      'update:selectedUserUuids',
      props.multiple ? [...props.selectedUserUuids, user.uuid] : [user.uuid]
    )

    query.value = ''
    activeIndex.value = 0
    if (!props.multiple) {
      showSuggestions.value = false
    }
  }

  const removeUser = (uuid: string): void => {
    emit(
      'update:selectedUserUuids',
      props.selectedUserUuids.filter((selectedUuid) => selectedUuid !== uuid)
    )
  }

  const addEmail = (): void => {
    if (props.disabled) {
      return
    }

    const value = query.value.trim()
    if (!props.allowEmails || !isEmail(value) || props.selectedEmails.includes(value)) {
      return
    }

    emit('update:selectedEmails', [...props.selectedEmails, value])
    query.value = ''
    activeIndex.value = 0
  }

  const removeEmail = (email: string): void => {
    emit(
      'update:selectedEmails',
      props.selectedEmails.filter((selectedEmail) => selectedEmail !== email)
    )
  }

  const onEnter = (): void => {
    if (filteredUsers.value.length > 0) {
      const selectedIndex = activeIndex.value >= 0 ? activeIndex.value : 0
      selectUser(filteredUsers.value[selectedIndex] ?? filteredUsers.value[0]!)
      return
    }

    addEmail()
  }

  const onComma = (): void => {
    if (canAddEmail.value) {
      addEmail()
    }
  }

  const onQueryKeydown = (event: KeyboardEvent): void => {
    if (event.key !== ',') {
      return
    }

    event.preventDefault()
    onComma()
  }

  const onBackspace = (): void => {
    if (query.value.length > 0) {
      return
    }

    if (props.selectedEmails.length > 0) {
      removeEmail(props.selectedEmails[props.selectedEmails.length - 1]!)
      return
    }

    if (props.selectedUserUuids.length > 0) {
      removeUser(props.selectedUserUuids[props.selectedUserUuids.length - 1]!)
    }
  }

  const moveActive = (delta: number): void => {
    const length = filteredUsers.value.length
    if (length === 0) {
      return
    }

    activeIndex.value = (activeIndex.value + delta + length) % length
  }

  const closeSuggestions = (): void => {
    window.setTimeout(() => {
      if (!wrapperRef.value?.contains(document.activeElement)) {
        showSuggestions.value = false
      }
    }, 120)
  }

  const onBlur = (): void => {
    if (canAddEmail.value) {
      addEmail()
    }
    closeSuggestions()
  }

  watch(query, (value) => {
    emit('update:query', value)
  })
</script>

<template>
  <div
    ref="wrapperRef"
    class="relative"
  >
    <div
      class="border-card-line flex min-h-[38px] cursor-text flex-wrap items-center gap-1.5 rounded-lg border
        bg-card px-1.5 py-1 focus-within:ring-2 focus-within:ring-primary/30"
      @click="focusInput"
    >
      <span
        v-for="user in selectedUsers"
        :key="user.uuid"
        class="bg-surface inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5 text-[11px] text-foreground"
      >
        <span
          class="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-primary text-[9px]
            font-medium text-primary-foreground"
        >
          {{ getInitials(userLabel(user)) }}
        </span>
        {{ userLabel(user) }}
        <button
          type="button"
          class="text-muted-foreground-1 hover:text-foreground"
          @click.stop="removeUser(user.uuid)"
        >
          <svg
            class="size-3 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </span>

      <span
        v-for="email in selectedEmails"
        :key="email"
        class="bg-surface inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5 text-[11px] text-foreground"
      >
        {{ email }}
        <button
          type="button"
          class="text-muted-foreground-1 hover:text-foreground"
          @click.stop="removeEmail(email)"
        >
          <svg
            class="size-3 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </span>

      <input
        v-if="multiple || selectedUserUuids.length === 0"
        ref="inputRef"
        v-model="query"
        type="text"
        :disabled="disabled"
        :class="[
          `placeholder:text-muted-foreground-1 focus:outline-hidden grow border-0 bg-transparent px-1 py-1
          text-xs text-foreground shadow-none focus:ring-0`,
          selectedUserUuids.length === 0 && selectedEmails.length === 0
            ? 'min-w-[100px]'
            : 'min-w-[24px]'
        ]"
        :placeholder="
          selectedUserUuids.length === 0 && selectedEmails.length === 0 ? placeholder : ''
        "
        @focus="showSuggestions = true"
        @blur="onBlur"
        @keydown.enter.prevent="onEnter"
        @keydown="onQueryKeydown"
        @keydown.down.prevent="moveActive(1)"
        @keydown.up.prevent="moveActive(-1)"
        @keydown.delete="onBackspace"
      />
    </div>

    <div
      v-if="showSuggestions && (filteredUsers.length > 0 || canAddEmail || loading)"
      class="border-dropdown-line bg-dropdown absolute z-40 mt-1 max-h-56 w-full overflow-auto rounded-lg border
        p-1 shadow-lg"
    >
      <div
        v-if="loading"
        class="text-muted-foreground-1 px-2.5 py-1.5 text-xs"
      >
        ...
      </div>
      <button
        v-for="(user, index) in filteredUsers"
        :key="user.uuid"
        type="button"
        :class="[
          'w-full rounded-md px-2.5 py-1.5 text-start text-xs',
          index === activeIndex
            ? 'bg-dropdown-item-hover text-foreground'
            : 'hover:bg-dropdown-item-hover text-foreground'
        ]"
        @mousedown.prevent="selectUser(user)"
        @mouseenter="activeIndex = index"
      >
        <span class="font-medium">{{ userLabel(user) }}</span>
        <span
          v-if="user.email"
          class="text-muted-foreground-1 block truncate text-[11px]"
        >
          {{ user.email }}
        </span>
      </button>

      <button
        v-if="canAddEmail"
        type="button"
        class="hover:bg-dropdown-item-hover focus:outline-hidden focus:bg-dropdown-item-focus w-full rounded-md
          px-2.5 py-1.5 text-start text-xs text-foreground"
        @mousedown.prevent="addEmail"
      >
        <span class="font-medium">{{ trimmedQuery }}</span>
        <span class="text-muted-foreground-1 block truncate text-[11px]">
          {{ addEmailLabel }}
        </span>
      </button>
    </div>
  </div>
</template>
